import { inject, Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import {
	TermTourismEventSet,
	TermTourismEventSetKinds,
	TourismEventsWithDelay,
	VehicleModel,
} from '@/bag/bag-transport-term/bag-transport-term.model';
import { BagTransportTermApiService } from '@/bag/bag-transport-term/bag-transport-term-api.service';
import { DateService } from '@/common/date.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { EventCategory } from '@/common/enums/EventCategory';

export interface ChosenTourismEvent {
	id: string;
	name: string;
	vehicle: string;
	crewCount: number;
	allSeatsIsTheSame: boolean;
	dateFrom?: string;
	dateTo?: string;
}

@Injectable({
	providedIn: 'root',
})
export class TourismEventConnectService {
	private readonly dateService = inject(DateService);
	private readonly infobox = inject(InfoboxService);
	private readonly bagTransportTermApiService = inject(BagTransportTermApiService);

	mapTourismEventsForAutocomplete(response: { data: TourismEventsWithDelay[] }): TourismEventsWithDelay[] {
		return response.data.map((event) => ({
			...event,
			displayName:
				event.date_from && event.date_to
					? `${event.name} (${this.dateService.apiToDateTime(event.date_from).date} - ${this.dateService.apiToDateTime(event.date_to).date})`
					: event.name,
		}));
	}

	buildTourismEventSearchParam(options?: { dateFrom?: string | null; dateTo?: string | null }): string {
		let params = new HttpParams()
			.set('showDeleted', '0')
			.set('on', '1')
			.set('category', `${EventCategory.OSKAR},${EventCategory.GROUP},${EventCategory.OSKAR_PLANE}`);

		if (options?.dateFrom) {
			params = params.set('date_from', options.dateFrom);
		}

		if (options?.dateTo) {
			params = params.set('date_to', options.dateTo);
		}

		return `${params.toString()}&search`;
	}

	buildChosenEvent(event: TourismEventsWithDelay, vehicles: VehicleModel[]): ChosenTourismEvent | null {
		const eventVehicleIds = event.vehicles.map((vehicle) => vehicle.vehicle_id);
		const latestVehicleId = eventVehicleIds[eventVehicleIds.length - 1];
		if (!latestVehicleId) {
			return null;
		}

		const vehicleSeats = vehicles
			.filter((vehicle) => eventVehicleIds.includes(vehicle.id as string))
			.map((vehicle) => vehicle.seats);

		return {
			id: `${event.id}`,
			name: event.name,
			vehicle: latestVehicleId,
			crewCount: event.countCrewToSeason,
			dateFrom: event.date_from ? this.dateService.apiToDateTime(event.date_from).date : undefined,
			dateTo: event.date_to ? this.dateService.apiToDateTime(event.date_to).date : undefined,
			allSeatsIsTheSame: this.allElementsEqual(vehicleSeats),
		};
	}

	async prepareTermTourismEventSet(
		chosenEvent: ChosenTourismEvent,
		options?: {
			existingEvents?: TermTourismEventSet[];
			kind?: TermTourismEventSetKinds;
		},
	): Promise<TermTourismEventSet | null> {
		const vehicleData = await this.bagTransportTermApiService.getVehicleData(chosenEvent.vehicle);
		const eventName = `${chosenEvent.name}, ${vehicleData.data.name}`;
		const existingEvents = options?.existingEvents ?? [];
		const filteredEvents = existingEvents.filter((event) => event.eventName === eventName);

		if (this.eventExistsOnDepartureAndReturn(filteredEvents)) {
			this.infobox.warning('Wybrane wydarzenie istnieje już na wyjeździe i powrocie');
			return null;
		}

		if (!chosenEvent.allSeatsIsTheSame) {
			this.infobox.warning(
				'To wydarzenie ma kilka przypisanych pojazdów o różnej wartości maksymalnych miejsc, do wydarzenia zostanie przypisana ilość siedzeń z najpóźniej jadącego pojazdu.',
			);
		}

		const seats = this.parseSeats(vehicleData.data.seats);
		if (seats === null) {
			this.infobox.warning('Ilość siedzeń jest nieprawidłowa');
			return null;
		}

		const kind = options?.kind ?? this.getKindToSet(filteredEvents);

		return {
			id: 0,
			eventId: chosenEvent.id,
			eventName,
			kind,
			maxSeat: seats + 2,
			crewCount: chosenEvent.crewCount || 6,
		};
	}

	checkEventKindExist(tourismEvents: TermTourismEventSet[], kind: TermTourismEventSetKinds): boolean {
		return tourismEvents.some((event) => event.kind === kind);
	}

	private eventExistsOnDepartureAndReturn(filteredEvents: TermTourismEventSet[]): boolean {
		return (
			this.checkEventKindExist(filteredEvents, TermTourismEventSetKinds.BOTH) ||
			(this.checkEventKindExist(filteredEvents, TermTourismEventSetKinds.DEPARTURE) &&
				this.checkEventKindExist(filteredEvents, TermTourismEventSetKinds.RETURN))
		);
	}

	private getKindToSet(filteredEvents: TermTourismEventSet[]): TermTourismEventSetKinds {
		if (this.checkEventKindExist(filteredEvents, TermTourismEventSetKinds.DEPARTURE)) {
			return TermTourismEventSetKinds.RETURN;
		}

		if (this.checkEventKindExist(filteredEvents, TermTourismEventSetKinds.RETURN)) {
			return TermTourismEventSetKinds.DEPARTURE;
		}

		return TermTourismEventSetKinds.BOTH;
	}

	private parseSeats(seats: number | string): number | null {
		if (typeof seats === 'number') {
			return seats;
		}

		const parsedSeats = Number(seats);
		if (isNaN(parsedSeats)) {
			return null;
		}

		return parsedSeats;
	}

	private allElementsEqual(values: unknown[]): boolean {
		if (values.length === 0) {
			return true;
		}

		return values.every((value) => value === values[0]);
	}
}
